import React, { useState, useContext, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Paper,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { ReactComponent as Line } from "assets/line.svg";
import { ReactComponent as Polygon } from "assets/polygon.svg";
import { ReactComponent as ImportIcon } from "assets/location_set_import.svg";
import styles from "./shape-list.module.scss";
import { useTranslation, Trans } from "react-i18next";
import ImportModal from "../import-modal/import-modal";
import EditShapeDialog from "../edit-shape-dialog";
import { Shape, DrawShapeSource } from "helpers/models";
import { AppContext, AppAction } from "helpers/context";
import { RightPanel } from "helpers/constants";
import { MapContext } from "helpers/map";
import ImportWitget from "../import-modal/import-modal-widget";
import {
  deleteShape,
  ImportShapeFeaturePayload,
  importShapeFeature,
  updateShape
} from "api/my-shape";
import china from "assets/json/c1.json";
import { getMapPathCoordinatesFromWgs84 } from "helpers/coordinate-transform";
import {
  areCoordinatesWithinShapeBounds,
  isPolygonCoordinatesSavable
} from "helpers/shape-coordinate-validation";

type SpatialType = "GCJ02" | "WGS84";

type Order = "asc" | "desc";
const SHAPE_TYPE_LINE = "Line";
const SHAPE_TYPE_REGION = "Region";
const DEFAULT_PROJECT_TYPE = "Ground";
const MAX_SHAPE_NAME_LENGTH = 180;
const NAME_COLUMN_WIDTH = 130;
const NAME_DISPLAY_MAX_LENGTH = 10;

const getDisplayShapeName = (name?: string) => {
  if (!name) {
    return "";
  }

  return name.length > NAME_DISPLAY_MAX_LENGTH
    ? `${name.slice(0, NAME_DISPLAY_MAX_LENGTH)}...`
    : name;
};

const ShapeList: React.FC = () => {
  const [context, dispatch] = useContext(AppContext);
  const { AMap, map } = useContext(MapContext);
  const [t] = useTranslation();
  const geodesicPolylineOptions = {
    geodesic: true
  };

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof Shape>("createTime");
  const [selected, setSelected] = useState<string[]>([]);

  // ===== Import Modal =====
  const [importOpen, setImportOpen] = useState(false);

  // ===== Create Shape Dialog (import) =====
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [createName, setCreateName] = useState("");
  const [category, setCategory] = useState<
    "polyline" | "polygon" | undefined
  >();
  const [createType, setCreateType] = useState<string>("Ground");
  const [feature, setFeature] = useState<any>();
  const [spatial, setSpatial] = useState<SpatialType>("WGS84");
  const [savingImportedShape, setSavingImportedShape] = useState(false);

  // ===== Row Edit/Delete Dialog (single instance) =====
  const [openRowEdit, setOpenRowEdit] = useState(false);
  const [editingRow, setEditingRow] = useState<Shape | null>(null);
  const [editRowName, setEditRowName] = useState("");
  const [editRowType, setEditRowType] = useState<string>("Ground");
  const [savingEditedShape, setSavingEditedShape] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deletingRow, setDeletingRow] = useState<Shape | null>(null);
  const [deletingShape, setDeletingShape] = useState(false);

  const chinaRings = (china as any)?.features?.[0]?.geometry?.rings || [];

  const isPointInRing = (point: number[], ring: number[][]) => {
    let inside = false;
    const x = point[0];
    const y = point[1];
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0];
      const yi = ring[i][1];
      const xj = ring[j][0];
      const yj = ring[j][1];
      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const isPointInChina = (point: number[]) => {
    return chinaRings.some((ring: number[][]) => isPointInRing(point, ring));
  };

  const getAllCoordinates = (geoFeature: any): number[][] => {
    const geometry = geoFeature?.geometry;
    if (!geometry) return [];
    if (geometry.type === "LineString") {
      return geometry.coordinates || [];
    }
    if (geometry.type === "Polygon") {
      return (geometry.coordinates || []).flat();
    }
    return [];
  };

  const detectSpatial = (geoFeature: any): SpatialType => {
    const coords = getAllCoordinates(geoFeature);
    if (!coords.length) return "WGS84";
    const allInsideChina = coords.every((p) => isPointInChina(p));
    return allInsideChina ? "GCJ02" : "WGS84";
  };

  const isValidImportedFeature = (parsedFeature: any) => {
    if (!parsedFeature?.geometry) return false;

    const geometryType = parsedFeature.geometry.type;
    const coordinates = parsedFeature.geometry.coordinates;

    if (geometryType === "LineString") {
      return (
        Array.isArray(coordinates) &&
        coordinates.length >= 2 &&
        coordinates.every(
          (point: any) =>
            Array.isArray(point) &&
            point.length >= 2 &&
            typeof point[0] === "number" &&
            typeof point[1] === "number"
        )
      );
    }

    if (geometryType === "Polygon") {
      return (
        Array.isArray(coordinates) &&
        coordinates.length === 1 &&
        Array.isArray(coordinates[0]) &&
        coordinates[0].length >= 4 &&
        coordinates[0].every(
          (point: any) =>
            Array.isArray(point) &&
            point.length >= 2 &&
            typeof point[0] === "number" &&
            typeof point[1] === "number"
        )
      );
    }

    return false;
  };

  const mapShapeTypeToProjectType = (shapeType: string) => {
    if (shapeType === "Aboveground") return "ABOVE" as const;
    if (shapeType === "Underground") return "UNDER" as const;
    return "GROUND" as const;
  };

  const normalizeProjectType = (value?: string) => {
    if (value === "Aboveground" || value === "Underground") {
      return value;
    }
    return DEFAULT_PROJECT_TYPE;
  };

  const getFeatureObject = (input: any) => {
    if (!input) return undefined;
    if (typeof input === "string") {
      try {
        return JSON.parse(input);
      } catch {
        return undefined;
      }
    }
    return input;
  };

  const handleParsedSuccess = ({
    category,
    feature,
    fileName
  }: {
    category: "polyline" | "polygon";
    feature: any;
    fileName: string;
  }) => {
    if (!feature || !isValidImportedFeature(feature)) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "unsupportedGeometryTitle",
          message: "unsupportedGeometryMessage"
        }
      });
      return;
    }

    setCategory(category);
    setCreateType(DEFAULT_PROJECT_TYPE);
    setFeature(feature);
    setSpatial(detectSpatial(feature));
    setCreateName(fileName);
    setOpenCreateDialog(true);
  };

  const handleOpenRowEdit = (row: Shape) => {
    setEditingRow(row);
    setEditRowName(row.name || "");
    setEditRowType(normalizeProjectType(row.type));
    setOpenRowEdit(true);
  };

  const handleOpenDelete = (row: Shape) => {
    setDeletingRow(row);
    setOpenDelete(true);
  };

  const handleRequestSort = (property: keyof Shape) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const resetAllShapeHighlight = () => {
    context.shapes.forEach((pl: any) => {
      if (pl?.aMapRef) {
        if (pl.source === DrawShapeSource.SHAPE_POLYGON) {
          pl.aMapRef.setOptions({
            strokeWeight: 2,
            fillOpacity: 0.2,
            cursor: "pointer"
          });
          return;
        }

        pl.aMapRef.setOptions({
          strokeWeight: 2,
          strokeStyle: "solid",
          cursor: "pointer",
          draggable: false
        });
      }
    });
  };

  const parseShapeGeometryCoordinates = (shape: any) => {
    if (!shape?.feature) return undefined;
    try {
      let geoJson =
        typeof shape.feature === "string"
          ? JSON.parse(shape.feature)
          : shape.feature;

      // 兼容后端返回被二次序列化的 geometryInfo
      if (typeof geoJson === "string") {
        geoJson = JSON.parse(geoJson);
      }

      // 兼容两种结构：Feature.geometry.coordinates 与 Geometry.coordinates
      const coordinates = Array.isArray(geoJson?.geometry?.coordinates)
        ? geoJson.geometry.coordinates
        : geoJson?.coordinates;

      if (!Array.isArray(coordinates) || coordinates.length < 1) {
        return undefined;
      }
      return coordinates;
    } catch {
      return undefined;
    }
  };

  const parseShapeLineCoordinates = (shape: any) => {
    const coordinates = parseShapeGeometryCoordinates(shape);
    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      return undefined;
    }

    if (
      !Array.isArray(coordinates[0]) ||
      typeof coordinates[0][0] !== "number"
    ) {
      return undefined;
    }

    return coordinates as number[][];
  };

  const parseShapePolygonRings = (shape: any) => {
    const coordinates = parseShapeGeometryCoordinates(shape);
    if (!Array.isArray(coordinates) || coordinates.length < 1) {
      return undefined;
    }

    if (!Array.isArray(coordinates[0]) || !Array.isArray(coordinates[0][0])) {
      return undefined;
    }

    return coordinates as number[][][];
  };

  const isSelectableShape = (shape: Shape) => {
    return (
      (shape.category === "polyline" || shape.category === "polygon") &&
      !!shape.feature
    );
  };

  const getSelectableShapes = () => {
    return context.myShapes.filter(isSelectableShape);
  };

  const getSelectedSelectableCount = () => {
    return selected.filter((id) =>
      context.myShapes.some(
        (shape) => shape.id === id && isSelectableShape(shape)
      )
    ).length;
  };

  const isHeaderChecked = () => {
    const totalSelectable = getSelectableShapes().length;
    const selectedSelectableCount = getSelectedSelectableCount();
    return totalSelectable > 0 && selectedSelectableCount === totalSelectable;
  };

  const isHeaderIndeterminate = () => {
    const totalSelectable = getSelectableShapes().length;
    const selectedSelectableCount = getSelectedSelectableCount();
    return (
      selectedSelectableCount > 0 && selectedSelectableCount < totalSelectable
    );
  };

  const getShapeTypeLabel = (row: Shape) => {
    const normalizedType =
      row.category === "polygon" ? SHAPE_TYPE_REGION : SHAPE_TYPE_LINE;
    return t(`myShapes.type-item.${normalizedType}`);
  };

  const getProjectTypeLabel = (row: Shape) => {
    if (row.category !== "polyline") {
      return "";
    }

    if (
      row.type === "Ground" ||
      row.type === "Aboveground" ||
      row.type === "Underground"
    ) {
      return t(`myShapes.type-item.${row.type}`);
    }

    return "";
  };

  const handleRowClick = (row: Shape) => {
    if (!selected.includes(row.id)) {
      return;
    }

    const shape = context.shapes.find((pl: any) => pl.shapeId === row.id);
    if (!shape?.aMapRef) {
      return;
    }

    resetAllShapeHighlight();
    if (row.category === "polygon") {
      shape.aMapRef.setOptions({
        strokeWeight: 4,
        fillOpacity: 0.3,
        cursor: "pointer"
      });
    } else {
      shape.aMapRef.setOptions({
        strokeWeight: 4,
        strokeStyle: "solid",
        cursor: "pointer"
      });
    }

    if (map) {
      map.setFitView([shape.aMapRef], true);
    }

    dispatch({ type: AppAction.setCurrentShape, payload: shape });
    dispatch({ type: AppAction.selectShape, payload: shape.id });

    const canRestoreAnalysisPanel =
      row.category === "polygon"
        ? !!shape.hasShapeAnalysed &&
          context.polygonAnalysisShapeId === row.id &&
          !!context.polygonAnalysisResult &&
          !!context.polygonAnalysisLegends &&
          !!context.polygonLayerStatistics &&
          context.polygonAnalysisStatus !== "running" &&
          context.polygonAnalysisStatus !== "error"
        : !!shape.hasShapeAnalysed &&
          context.lineAnalysisShapeId === row.id &&
          !!context.lineAnalysisResult &&
          !!context.lineAnalysisLegends &&
          context.lineAnalysisStatus !== "running" &&
          context.lineAnalysisStatus !== "error";

    if (canRestoreAnalysisPanel) {
      dispatch({
        type: AppAction.setRightPanel,
        payload:
          row.category === "polygon" ? RightPanel.Polygon : RightPanel.Line
      });
    }
  };

  // 从全局 shapes 中排除指定 shapeId 对应的线段数据。
  const filterOutShapeEntries = (shapeIds: string[]) => {
    return context.shapes.filter(
      (pl: any) => !(pl.shapeId && shapeIds.includes(pl.shapeId))
    );
  };

  // 从地图上移除指定 shapeId 对应的线段实例（仅移除形状，不直接改状态）。
  const removeShapeEntriesFromMap = (shapeIds: string[]) => {
    context.shapes.forEach((pl: any) => {
      if (pl.shapeId && shapeIds.includes(pl.shapeId) && pl.aMapRef && map) {
        map.remove(pl.aMapRef);
      }
    });
  };

  // 按统一规则创建 shape 对应的地图形状：
  // - 负责坐标解析与校验
  // - 负责地图对象创建与上图
  // - 返回可直接写入状态的形状数据
  const createShapeMapData = (
    shape: Shape,
    strokeWeight: number,
    state: "selected" | "idle",
    enableInvalidLog = false
  ) => {
    // 地图构造器或地图实例未就绪时，不创建线段。
    if (!AMap || !map) {
      // 返回 undefined 交由调用方决定是否中断流程。
      return undefined;
    }

    if (shape.category === "polygon") {
      const rings = parseShapePolygonRings(shape);
      if (!Array.isArray(rings) || rings.length < 1) {
        if (enableInvalidLog) {
          console.warn("Invalid polygon rings:", rings);
        }
        return undefined;
      }

      const mapRings = rings.map((ring) =>
        getMapPathCoordinatesFromWgs84(ring, shape.spatial)
      );

      const polygon = new AMap.Polygon({
        path: mapRings,
        strokeColor: "rgb(15, 77, 188)",
        strokeWeight,
        fillColor: "rgb(15, 77, 188)",
        fillOpacity: state === "selected" ? 0.3 : 0.2,
        cursor: "pointer",
        bubble: true
      });

      map.add(polygon);

      return {
        shapeData: {
          id: shape.id,
          source: DrawShapeSource.SHAPE_POLYGON,
          name: shape.name,
          type: shape.type,
          coordinates: rings[0],
          state: state as any,
          aMapRef: polygon,
          shapeId: shape.id,
          spatial: shape.spatial,
          hasShapeAnalysed: shape.hasShapeAnalysed
        },
        aMapRef: polygon
      };
    }

    // 解析 shape 中保存的 GeoJSON/geometry 坐标。
    const coordinates = parseShapeLineCoordinates(shape);
    // 保护分支：坐标无效或点数不足两点时，不创建线。
    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      // 按需输出无效坐标日志，便于单选场景调试。
      if (enableInvalidLog) {
        // 打印解析失败的坐标内容。
        console.warn("Invalid coordinates:", coordinates);
      }
      // 坐标不合法时返回 undefined。
      return undefined;
    }

    // 当输入坐标系为 WGS-84 时，逐点转换为 GCJ-02 后再交给地图绘制
    //  当输入为 GCJ-02 或未声明坐标系时，默认认为已是可绘制坐标，直接返回原值。
    const mapPathCoordinates = getMapPathCoordinatesFromWgs84(
      // 原始坐标数组。
      coordinates,
      // 当前 shape 的坐标系信息。
      shape.spatial
    );

    // 创建地图线段实例，并套用传入的样式参数。
    const pl = new AMap.Polyline({
      // 地图展示路径。
      path: mapPathCoordinates,
      // 线条颜色。
      strokeColor: "rgb(15, 77, 188)",
      // 线宽由调用方决定，用于区分 selected/idle。
      strokeWeight,
      // 使用实线样式。
      strokeStyle: "solid",
      // 鼠标样式为可点击指针。
      cursor: "pointer",
      // 允许事件冒泡。
      bubble: true,
      // 保留地理大圆线等公共配置。
      ...geodesicPolylineOptions
    });

    // 将新建线段立即添加到地图。
    map.add(pl);

    // 返回状态层可直接使用的数据，以及地图实例引用。
    return {
      // shapeData 用于写入全局 shapes 状态。
      shapeData: {
        // 直接复用业务 shape.id 作为 polyline.id。
        id: shape.id,
        // 标记该线段来源于 my-shapes 列表。
        source: DrawShapeSource.SHAPE_LINE,
        // 线段名称。
        name: shape.name,
        // 线段类型。
        type: shape.type,
        // 原始坐标数据。
        coordinates,
        // 线段状态，由调用方传入 selected 或 idle。
        state: state as any,
        // 地图对象引用，便于后续高亮、删除、缩放等操作。
        aMapRef: pl,
        // 回填关联的 shapeId。
        shapeId: shape.id,
        // 回填坐标系信息。
        spatial: shape.spatial,
        hasShapeAnalysed: shape.hasShapeAnalysed
      },
      // 单独返回地图实例，方便调用方直接 setFitView。
      aMapRef: pl
    };
  };

  const restoreShapeAnalysisPanel = (shapeData: any) => {
    dispatch({ type: AppAction.setCurrentShape, payload: shapeData });

    const isPolygon = shapeData?.source === DrawShapeSource.SHAPE_POLYGON;
    const canRestoreAnalysisPanel = isPolygon
      ? !!shapeData?.hasShapeAnalysed &&
        context.polygonAnalysisShapeId === shapeData?.id &&
        !!context.polygonAnalysisResult &&
        !!context.polygonAnalysisLegends &&
        !!context.polygonLayerStatistics &&
        context.polygonAnalysisStatus !== "running" &&
        context.polygonAnalysisStatus !== "error"
      : !!shapeData?.hasShapeAnalysed &&
        context.lineAnalysisShapeId === shapeData?.id &&
        !!context.lineAnalysisResult &&
        !!context.lineAnalysisLegends &&
        context.lineAnalysisStatus !== "running" &&
        context.lineAnalysisStatus !== "error";

    if (canRestoreAnalysisPanel) {
      dispatch({
        type: AppAction.setRightPanel,
        payload: isPolygon ? RightPanel.Polygon : RightPanel.Line
      });
    }
  };

  // 处理 shape 列表单条勾选/取消勾选：
  // 1) 先同步表格勾选状态；
  // 2) 若为取消勾选，则移除地图线段并清空当前选中；
  // 3) 若为新勾选，则解析坐标、创建并上图 polyline、写回全局状态并设置为当前选中。
  // 入参 id 为当前操作的 shapeId。
  const handleSelect = (id: string) => {
    // 判断当前点击的 shape 是否已经处于勾选状态。
    const isCurrentlySelected = selected.includes(id);

    // 先同步表格勾选状态：已选则移除，未选则加入。
    setSelected((prev) =>
      // 使用条件表达式生成新的 selected 数组。
      isCurrentlySelected ? prev.filter((item) => item !== id) : [...prev, id]
    );

    // 如果当前是“已选中”，本次操作即“取消选中”。
    if (isCurrentlySelected) {
      // 取消选中：从 shapes 中查找并移除对应线段。
      const shapeToRemove = context.shapes.find(
        // 通过 shapeId 与当前 id 匹配对应 polyline。
        (pl) => pl.shapeId === id
      );
      // 找到对应 polyline 后再执行地图和状态清理。
      if (shapeToRemove) {
        // 地图实例与线段实例都存在时，从地图移除该线段。
        if (shapeToRemove.aMapRef && map) {
          // 从 AMap 中移除当前 polyline 形状对象。
          map.remove(shapeToRemove.aMapRef);
        }
        // 从全局 shapes 状态中删除对应数据项。
        dispatch({
          // 使用 deleteShape 动作删除目标 polyline。
          type: AppAction.deleteShape,
          // 传入要删除的 polyline id。
          payload: shapeToRemove.id
        });
      }
      // 清空当前选中的 polyline，避免右侧详情保留旧选中项。
      dispatch({ type: AppAction.deselectShape });
      // 清空当前 shape 引用，触发 polygon-results.tsx 的 useEffect 清除 H3 六边形等关联覆盖物。
      dispatch({ type: AppAction.setCurrentShape, payload: undefined });
      // 取消选中流程到此结束。
      return;
    }

    // 新选中前先重置所有已上图线段的高亮样式。
    resetAllShapeHighlight();

    // 从 myShapes 中找到当前选中的 shape 实体。
    const selectedShape = context.myShapes.find((shape) => shape.id === id);
    // 保护分支：无 feature 或地图未就绪时直接返回。
    if (
      // 需要确保 shape 存在可解析的 feature。
      !selectedShape?.feature ||
      // AMap 构造器未就绪时不继续。
      !AMap ||
      // map 实例未就绪时不继续。
      !map
    )
      // 前置条件不满足，终止本次选中流程。
      return;

    // TODO: 预留后续在选中时补充业务逻辑。

    // 若同一 shape 已存在上图线段，则先移除旧实例，确保“一 shape 一条线”。
    removeShapeEntriesFromMap([id]);

    // 过滤掉当前 shape 的旧数据，后续用新数据覆盖。
    const baseShapes = filterOutShapeEntries([id]);

    // 按单选样式创建新形状数据。
    const created = createShapeMapData(selectedShape, 4, "selected", true);
    if (!created) {
      return;
    }

    // 自动缩放视图到该线段。
    map.setFitView([created.aMapRef], true);

    // 直接复用业务 shapeId 作为 polyline.id，保持标识一致。
    const shapeEntryId = selectedShape.id;

    // 在清理后的 shapes 基础上追加新线段。
    const nextShapes = [...baseShapes, created.shapeData];
    // 将新的 shapes 列表写回全局状态。
    dispatch({
      // 使用 setShapes 动作整体更新。
      type: AppAction.setShapes,
      // 传入更新后的完整数组。
      payload: nextShapes
    });
    restoreShapeAnalysisPanel(created.shapeData);
    // 选中后触发右侧详情面板联动（polyline 和 polygon 均适用）。
    dispatch({
      type: AppAction.selectShape,
      payload: shapeEntryId
    });
  };

  const handleSelectAll = () => {
    // 全选入口说明：
    // 1) 若当前已全选，则执行“反选”为全不选（清除地图中的 shape 线段并取消勾选）。
    // 2) 若当前未全选，则批量把 my-shapes 中可选线段上图，并让地图缩放到全部线段范围。
    // 3) 全选仅用于批量展示，不设置 selectedShapeId，避免触发 LineInfo 面板。
    const selectableShapes = getSelectableShapes();
    const selectableIds = selectableShapes.map((shape) => shape.id);
    const selectedInSelectable = selected.filter((id) =>
      selectableIds.includes(id)
    );
    // 仅当“可选项数量 > 0 且已勾选数量 == 可选项数量”时判定为已全选。
    const allSelected =
      selectableIds.length > 0 &&
      selectedInSelectable.length === selectableIds.length;

    // 已全选时再次点击：执行清空流程
    // - 移除地图中由 shape-list 上图的对应 polyline
    // - 同步更新全局 polylines（保留其他来源线段）
    // - 清空 selectedShapeId，防止右侧线详情面板残留
    // - 清空当前 shape 引用，触发 polygon-results 的 useEffect 清除 H3 六边形
    // - 更新表格勾选状态（仅移除当前可选集合）
    if (allSelected) {
      removeShapeEntriesFromMap(selectableIds);

      dispatch({
        type: AppAction.setShapes,
        payload: filterOutShapeEntries(selectableIds)
      });
      dispatch({ type: AppAction.deselectShape });
      dispatch({ type: AppAction.setCurrentShape, payload: undefined });
      setSelected((prev) => prev.filter((id) => !selectableIds.includes(id)));
      return;
    }

    // 保护分支：地图实例未就绪或没有可上图线段时，不做任何处理。
    if (!AMap || !map || !selectableShapes.length) {
      return;
    }

    // 先恢复现有线段样式，避免残留高亮状态影响全选展示。
    resetAllShapeHighlight();

    // 去重处理：移除当前可选 shape 已存在的上图线段，避免重复叠加。
    removeShapeEntriesFromMap(selectableIds);

    // 基于“已去除当前可选集合”的状态作为批量创建的基线数据。
    const baseShapes = filterOutShapeEntries(selectableIds);

    const createdShapes: any[] = [];
    const validSelectedIds: string[] = [];

    // 批量创建并上图所有可选线段：
    // - 样式统一为普通态（strokeWeight=2）
    // - state 统一为 idle，不进入单条“选中态”
    // - 仅记录解析成功的 shapeId 用于更新勾选状态
    selectableShapes.forEach((shape) => {
      const created = createShapeMapData(shape, 2, "idle");
      if (!created) {
        return;
      }

      createdShapes.push(created.shapeData);
      validSelectedIds.push(shape.id);
    });

    // 合并基线与本次新建线段，形成最终全量状态。
    const nextShapes = [...baseShapes, ...createdShapes];
    dispatch({
      type: AppAction.setShapes,
      payload: nextShapes
    });

    // 批量展示后自动缩放到当前全部可视线段范围，确保“全选即全元素可见”。
    const fitViewTargets = nextShapes
      .map((pl: any) => pl.aMapRef)
      .filter((item: any) => !!item);
    if (fitViewTargets.length > 0) {
      // 传 false 使用平滑过渡，避免“瞬间跳转”体验。
      map.setFitView(fitViewTargets, false);
    }

    // 不设置 selectedShapeId，确保 LineInfo 不展示。
    dispatch({ type: AppAction.deselectShape });

    // 勾选状态与“成功上图”的 shape 保持一致，避免解析失败项被误勾选。
    setSelected((prev) => {
      const nonSelectable = prev.filter((id) => !selectableIds.includes(id));
      return [...nonSelectable, ...validSelectedIds];
    });
  };

  useEffect(() => {
    const visibleShapeIds = Array.from(
      new Set(
        context.shapes
          .map((pl: any) => pl.shapeId)
          .filter((id: any) => typeof id === "string")
      )
    ) as string[];

    setSelected(
      visibleShapeIds.filter((id) =>
        context.myShapes.some((shape) => shape.id === id)
      )
    );
  }, [context.myShapes, context.shapes]);

  const sortedRows = [...context.myShapes].sort((a, b) => {
    if (orderBy === "createTime") {
      const aTime = a.createTime ?? 0;
      const bTime = b.createTime ?? 0;
      if (aTime < bTime) return order === "asc" ? -1 : 1;
      if (aTime > bTime) return order === "asc" ? 1 : -1;
      return 0;
    }

    const aValue = a[orderBy] ?? "";
    const bValue = b[orderBy] ?? "";

    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });

  const categoryIconMap: Record<string, React.ReactNode> = {
    polygon: <Polygon width="24px" height="24px" />,
    polyline: <Line width="24px" height="24px" />
  };

  const getSelectedShapeId = () => {
    const selectedPolyline = context.shapes.find(
      (pl: any) => pl.id === context.selectedShapeId
    );
    return selectedPolyline?.shapeId;
  };

  const renderRowActions = (row: Shape) => {
    return (
      <div
        className={`${styles.icons} icons-set`}
        onClick={(evt) => evt.stopPropagation()}
      >
        <Tooltip title={t("tooltip.edit")}>
          <IconButton
            style={{ width: "36px", height: "36px" }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenRowEdit(row);
            }}
          >
            <span className="material-icons">create</span>
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip.delete")}>
          <IconButton
            style={{ width: "36px", height: "36px" }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDelete(row);
            }}
          >
            <span className="material-icons">delete</span>
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  const syncShapeContext = (shapeId: string, name: string, type?: string) => {
    dispatch({
      type: AppAction.setShapes,
      payload: context.shapes.map((pl: any) =>
        pl.shapeId === shapeId
          ? {
              ...pl,
              name,
              type
            }
          : pl
      )
    });
  };

  const cleanupShapes = (shapeIds: string[]) => {
    const shapesToRemove = context.shapes.filter(
      (pl: any) => pl.shapeId && shapeIds.includes(pl.shapeId)
    );

    shapesToRemove.forEach((pl: any) => {
      if (pl.aMapRef && map) {
        map.remove(pl.aMapRef);
      }
    });

    const nextShapes = context.shapes.filter(
      (pl: any) => !(pl.shapeId && shapeIds.includes(pl.shapeId))
    );
    dispatch({ type: AppAction.setShapes, payload: nextShapes });

    const removedSelected = shapesToRemove.some(
      (pl: any) => pl.id === context.selectedShapeId
    );
    if (removedSelected) {
      dispatch({ type: AppAction.deselectShape });
      dispatch({ type: AppAction.setCurrentShape, payload: undefined });
      dispatch({
        type: AppAction.setRightPanel,
        payload: context.currentMultipleLocations
          ? RightPanel.Multiple
          : RightPanel.Empty
      });
    }
  };

  const handleSaveRowEdit = async () => {
    if (savingEditedShape) return;
    if (!editingRow) return;

    const trimmedName = editRowName.trim();
    const parsedFeature = getFeatureObject(editingRow.feature);
    const geometryInfo = parsedFeature?.geometry
      ? parsedFeature.geometry
      : parsedFeature;
    if (!trimmedName || !geometryInfo) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          message: "shapeEditFailed"
        }
      });
      return;
    }

    if (trimmedName.length > MAX_SHAPE_NAME_LENGTH) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          message: "shapeNameTooLongMessage"
        }
      });
      return;
    }

    try {
      setSavingEditedShape(true);
      const result = await updateShape(editingRow.id, {
        id: editingRow.id,
        sourceId: "",
        sourceType: editingRow.sourceType || "INPUT",
        geometryType:
          editingRow.category === "polygon" ? "POLYGON" : "POLYLINE",
        // TODO: 后端 polygon 专用字段协议完成后，这里需要替换为面类型专用 projectType 映射。
        projectType:
          editingRow.category === "polygon"
            ? "GROUND"
            : mapShapeTypeToProjectType(editRowType),
        shapeName: trimmedName,
        spatial: editingRow.spatial,
        geometryInfo: JSON.stringify(geometryInfo)
      });

      if (!result || result.code !== 200) {
        const editErrorMessage = result?.message || result?.msg;
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: editErrorMessage
            ? {
                titleIcon: "error",
                titleIconColor: "red",
                resMessage: editErrorMessage
              }
            : {
                titleIcon: "error",
                titleIconColor: "red",
                message: "shapeEditFailed"
              }
        });
        return;
      }

      setOpenRowEdit(false);
      dispatch({
        type: AppAction.updateMyShape,
        payload: {
          id: editingRow.id,
          name: trimmedName,
          type:
            editingRow.category === "polygon"
              ? SHAPE_TYPE_REGION
              : normalizeProjectType(editRowType)
        }
      });
      syncShapeContext(
        editingRow.id,
        trimmedName,
        editingRow.category === "polygon"
          ? SHAPE_TYPE_REGION
          : normalizeProjectType(editRowType)
      );
    } catch {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          message: "shapeEditFailed"
        }
      });
    } finally {
      setSavingEditedShape(false);
    }
  };

  const handleSaveImportedShape = async () => {
    if (savingImportedShape) return;

    // 保存前做一次兜底校验，避免脏数据进入后端
    const trimmedName = createName.trim();
    const parsedFeature = getFeatureObject(feature);
    if (
      !trimmedName ||
      !parsedFeature ||
      !category ||
      !isValidImportedFeature(parsedFeature)
    ) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "unsupportedGeometryTitle",
          message: "unsupportedGeometryMessage"
        }
      });
      return;
    }

    if (trimmedName.length > MAX_SHAPE_NAME_LENGTH) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          message: "shapeNameTooLongMessage"
        }
      });
      return;
    }

    if (
      !areCoordinatesWithinShapeBounds(parsedFeature?.geometry?.coordinates)
    ) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "invalidCoordinateBoundsTitle",
          message: "invalidCoordinateBoundsMessage"
        }
      });
      return;
    }

    if (
      category === "polygon" &&
      !isPolygonCoordinatesSavable(parsedFeature?.geometry?.coordinates)
    ) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "invalidPolygonForSaveTitle",
          message: "invalidPolygonForSaveMessage"
        }
      });
      return;
    }

    // 组装后端 import/feature 接口请求体
    const projectType = mapShapeTypeToProjectType(createType);
    const importPayload = {
      type: "Feature" as const,
      properties: {
        sourceId: "",
        sourceType: "IMPORT" as const,
        geometryType: (category === "polygon" ? "POLYGON" : "POLYLINE") as
          | "POLYLINE"
          | "POLYGON",
        // TODO: 后端 polygon 入库专用协议完成后，这里改为面专用接口字段与枚举。
        projectType: category === "polygon" ? "GROUND" : projectType,
        shapeName: trimmedName,
        spatial
      },
      geometry: {
        type:
          category === "polygon"
            ? ("Polygon" as const)
            : ("LineString" as const),
        coordinates: parsedFeature.geometry.coordinates as
          | number[][]
          | number[][][]
      }
    } as ImportShapeFeaturePayload;

    try {
      setSavingImportedShape(true);

      const result = await importShapeFeature(importPayload);
      const id: string | undefined = result?.data?.id;
      const createTime: number | undefined = result?.data?.createTime;
      if (!result || result.code !== 200 || !id) {
        const importErrorMessage = result?.message || result?.msg;
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: importErrorMessage
            ? {
                titleIcon: "error",
                titleIconColor: "red",
                title: "polygonHoleTitle",
                resMessage: importErrorMessage
              }
            : {
                titleIcon: "error",
                titleIconColor: "red",
                title: "polygonHoleTitle",
                message: "shapeImportFailed"
              }
        });
        return;
      }

      setOpenCreateDialog(false);

      const featureStr = JSON.stringify(parsedFeature);
      const shape = {
        id,
        name: trimmedName,
        category,
        type: category === "polygon" ? SHAPE_TYPE_REGION : createType,
        spatial,
        feature: featureStr,
        createTime
      };

      dispatch({
        type: AppAction.addMyShapes,
        payload: shape
      });

      // 新增数据默认勾选：同步上图并选中
      if (AMap && map) {
        const created = createShapeMapData(shape as Shape, 4, "selected", true);
        if (created) {
          map.setFitView([created.aMapRef], true);

          dispatch({
            type: AppAction.setShapes,
            payload: [...context.shapes, created.shapeData]
          });

          if (shape.category === "polyline") {
            dispatch({
              type: AppAction.selectShape,
              payload: id
            });
          } else {
            dispatch({ type: AppAction.deselectShape });
          }
        }
      }

      setSelected((prev) => (prev.includes(id) ? prev : [...prev, id]));
    } catch {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "polygonHoleTitle",
          message: "shapeImportFailed"
        }
      });
    } finally {
      setSavingImportedShape(false);
    }
  };

  return (
    <div className={styles.shapeList}>
      {context.myShapes.length > 0 ? (
        <>
          <Paper className={styles.table}>
            <TableContainer
              sx={{
                height: "100%",
                overflowX: "auto",
                overflowY: "auto",
                flex: 1
              }}
            >
              <Table
                stickyHeader
                aria-label="sticky  table"
                size="small"
                style={{
                  tableLayout: "fixed",
                  width: "100%",
                  minWidth: "400px"
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ padding: 0, width: 60 }} align="left">
                      <Checkbox
                        color="success"
                        checked={isHeaderChecked()}
                        indeterminate={isHeaderIndeterminate()}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{
                        width: "72px",
                        minWidth: "72px",
                        whiteSpace: "nowrap",
                        padding: "6px"
                      }}
                      sortDirection={orderBy === "category" ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === "category"}
                        direction={order}
                        onClick={() => handleRequestSort("category")}
                      >
                        {t("myShapes.category")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align="left"
                      style={{
                        width: NAME_COLUMN_WIDTH,
                        maxWidth: NAME_COLUMN_WIDTH,
                        whiteSpace: "nowrap",
                        padding: "6px"
                      }}
                      sortDirection={orderBy === "name" ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === "name"}
                        direction={order}
                        onClick={() => handleRequestSort("name")}
                      >
                        {t("myShapes.name")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      style={{
                        width: "96px",
                        minWidth: "96px",
                        whiteSpace: "nowrap",
                        padding: "6px"
                      }}
                      align="left"
                    >
                      {t("myShapes.type")}
                    </TableCell>
                    <TableCell
                      style={{
                        width: "120px",
                        minWidth: "120px",
                        whiteSpace: "nowrap",
                        padding: "6px"
                      }}
                      align="left"
                    >
                      {t("myShapes.projectType")}
                    </TableCell>
                    <TableCell style={{ padding: 0, width: 0 }} />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      className={styles.row}
                      selected={getSelectedShapeId() === row.id}
                      onClick={() => handleRowClick(row)}
                      sx={{
                        "&.Mui-selected,&.Mui-selected:hover": {
                          backgroundColor: "rgba(14, 138, 63, 0.08)!important"
                        },
                        "&.Mui-selected .icons-set": {
                          background:
                            "linear-gradient(90deg,rgba(236,246,240,0) 0,#ecf6f0 24px)!important"
                        }
                      }}
                    >
                      <TableCell style={{ padding: 0 }}>
                        <Checkbox
                          color="success"
                          checked={selected.includes(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleSelect(row.id)}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          width: "72px",
                          minWidth: "72px",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {categoryIconMap[row.category]}
                      </TableCell>
                      <TableCell
                        style={{
                          width: NAME_COLUMN_WIDTH,
                          maxWidth: NAME_COLUMN_WIDTH,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        title={row.name}
                      >
                        {getDisplayShapeName(row.name)}
                      </TableCell>
                      <TableCell
                        style={{
                          width: "96px",
                          minWidth: "96px",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {getShapeTypeLabel(row)}
                      </TableCell>
                      <TableCell
                        style={{
                          width: "120px",
                          minWidth: "120px",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {getProjectTypeLabel(row)}
                      </TableCell>
                      <TableCell style={{ padding: 0 }}>
                        {renderRowActions(row)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <div className={styles.footer}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                startIcon={
                  <ImportIcon
                    style={{ width: "18px", height: "18px", fill: "white" }}
                  />
                }
                onClick={() => setImportOpen(true)}
              >
                {t("buttons.importData")}
              </Button>
            </Stack>
          </div>
        </>
      ) : (
        <div
          style={{
            marginTop: 80,
            width: "90%",
            height: "60%"
          }}
        >
          <div
            style={{
              marginBottom: 20,
              textAlign: "center",
              fontSize: "16px"
            }}
          >
            {t("myShapes.modal-gson.how")}
          </div>
          <ImportWitget onParsedSuccess={handleParsedSuccess} />
        </div>
      )}
      {/* ✅ Import Modal */}
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onParsedSuccess={({ category, feature, fileName }) => {
          setImportOpen(false); // ✅ 先关 ImportModal
          handleParsedSuccess({ category, feature, fileName });
        }}
      />

      {/* Row Edit Dialog (single instance) */}
      <EditShapeDialog
        open={openRowEdit}
        onClose={() => setOpenRowEdit(false)}
        onSave={handleSaveRowEdit}
        editName={editRowName}
        setEditName={setEditRowName}
        category={editingRow?.category}
        type={editRowType}
        setType={setEditRowType}
        saveLoading={savingEditedShape}
        saveDisabled={savingEditedShape}
      />

      {/* Row Delete Dialog (single instance) */}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={openDelete}
        onClose={() => setOpenDelete(false)}
      >
        <DialogTitle>{t("myShapes.delete-shape")}</DialogTitle>
        <DialogContent>
          <Trans
            i18nKey="myShapes.del-tip"
            values={{ name: deletingRow?.name || "" }}
            components={{ strong: <strong /> }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="success"
            disabled={deletingShape}
            onClick={() => setOpenDelete(false)}
          >
            {t("myShapes.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingShape}
            onClick={async () => {
              if (deletingShape) return;
              if (!deletingRow) return;

              try {
                setDeletingShape(true);
                const result = await deleteShape(deletingRow.id);
                if (!result || result.code !== 200) {
                  const deleteErrorMessage = result?.message || result?.msg;
                  dispatch({
                    type: AppAction.addSnackBarParams,
                    payload: deleteErrorMessage
                      ? {
                          titleIcon: "error",
                          titleIconColor: "red",
                          resMessage: deleteErrorMessage
                        }
                      : {
                          titleIcon: "error",
                          titleIconColor: "red",
                          message: "shapeDeleteFailed"
                        }
                  });
                  return;
                }

                setOpenDelete(false);
                cleanupShapes([deletingRow.id]);
                dispatch({
                  type: AppAction.deleteMyShapes,
                  payload: [deletingRow.id]
                });
                setSelected((prev) =>
                  prev.filter((id) => id !== deletingRow.id)
                );
              } catch {
                dispatch({
                  type: AppAction.addSnackBarParams,
                  payload: {
                    titleIcon: "error",
                    titleIconColor: "red",
                    message: "shapeDeleteFailed"
                  }
                });
              } finally {
                setDeletingShape(false);
              }
            }}
          >
            {t("myShapes.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Shape Dialog (import flow) */}
      <EditShapeDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onSave={handleSaveImportedShape}
        editName={createName}
        setEditName={setCreateName}
        category={category}
        type={createType}
        setType={setCreateType}
        spatial={spatial}
        setSpatial={setSpatial}
        saveLoading={savingImportedShape}
        saveDisabled={savingImportedShape}
      />
    </div>
  );
};

export default ShapeList;
